import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { apiGet } from '../utils/apiClient';
import {
  CalendarDateRange,
  CalendarViewMode,
  ClassCalendarFilters,
  ClassCalendarItem,
  ClassCalendarMetadata,
  ClassCalendarResponse,
} from '../types/ClassCalendar.type';
import {
  addCalendarDays,
  addCalendarMonths,
  dateFromKey,
  dateKeyFromDate,
  getCalendarDateRange,
} from '../utils/classCalendar';
import { buildClassCalendarParams } from '../utils/classCalendarQuery';

type CalendarRole = 'admin' | 'coach';

interface UseClassCalendarOptions {
  role: CalendarRole;
  enabled: boolean;
  filters: ClassCalendarFilters;
}

interface UseClassCalendarResult {
  view: CalendarViewMode;
  setView: (view: CalendarViewMode) => void;
  anchorDate: string;
  range: CalendarDateRange;
  items: ClassCalendarItem[];
  metadata: ClassCalendarMetadata | null;
  loading: boolean;
  error: string | null;
  goToToday: () => void;
  goToDate: (dateKey: string) => void;
  goToPrevious: () => void;
  goToNext: () => void;
  refresh: () => void;
}

const getInitialDateKey = (): string => dateKeyFromDate(new Date());

const addNavigationPeriod = (
  anchorDate: string,
  view: CalendarViewMode,
  direction: number,
): string => {
  const date = dateFromKey(anchorDate);
  const nextDate =
    view === 'month'
      ? addCalendarMonths(date, direction)
      : addCalendarDays(date, direction * 7);
  return dateKeyFromDate(nextDate);
};

export const useClassCalendar = ({
  role,
  enabled,
  filters,
}: UseClassCalendarOptions): UseClassCalendarResult => {
  const [view, setView] = useState<CalendarViewMode>('week');
  const [anchorDate, setAnchorDate] = useState(getInitialDateKey);
  const [items, setItems] = useState<ClassCalendarItem[]>([]);
  const [metadata, setMetadata] = useState<ClassCalendarMetadata | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [refreshToken, setRefreshToken] = useState(0);
  const requestSequence = useRef(0);

  const range = useMemo(
    () => getCalendarDateRange(anchorDate, view),
    [anchorDate, view],
  );
  const filterSignature = useMemo(() => JSON.stringify(filters), [filters]);

  useEffect(() => {
    if (!enabled) return undefined;

    const controller = new AbortController();
    const sequence = ++requestSequence.current;
    const loadCalendar = async () => {
      setLoading(true);
      setError(null);

      const params = buildClassCalendarParams(range, filters);

      const endpoint =
        role === 'coach' ? '/class/coach/calendar' : '/class/calendar';

      try {
        const response = await apiGet<ClassCalendarResponse>(
          `${endpoint}?${params.toString()}`,
          { signal: controller.signal },
        );

        if (controller.signal.aborted || sequence !== requestSequence.current) {
          return;
        }

        setItems(response.data || []);
        setMetadata(response.metadata || null);
      } catch (requestError) {
        if (controller.signal.aborted || sequence !== requestSequence.current) {
          return;
        }

        const message =
          requestError instanceof Error
            ? requestError.message
            : 'Unable to load class calendar.';
        setError(message);
        setItems([]);
        setMetadata(null);
      } finally {
        if (
          !controller.signal.aborted &&
          sequence === requestSequence.current
        ) {
          setLoading(false);
        }
      }
    };

    void loadCalendar();
    return () => controller.abort();
  }, [enabled, filters, filterSignature, range, refreshToken, role]);

  const goToToday = useCallback(() => {
    setAnchorDate(getInitialDateKey());
  }, []);

  const goToDate = useCallback((dateKey: string) => {
    setAnchorDate(dateKey);
  }, []);

  const goToPrevious = useCallback(() => {
    setAnchorDate((current) => addNavigationPeriod(current, view, -1));
  }, [view]);

  const goToNext = useCallback(() => {
    setAnchorDate((current) => addNavigationPeriod(current, view, 1));
  }, [view]);

  const refresh = useCallback(() => {
    setRefreshToken((current) => current + 1);
  }, []);

  return {
    view,
    setView,
    anchorDate,
    range,
    items,
    metadata,
    loading,
    error,
    goToToday,
    goToDate,
    goToPrevious,
    goToNext,
    refresh,
  };
};
